/**
 *  @class
 *  @function CartRemove
 *
 *  A configured cake's delete button carries `data-group-keys` (the cake line AND every
 *  surcharge line sharing its `_Cake_ID`, see snippets/bh-cart-cake-actions.liquid) and
 *  removes them in ONE cart/update call. Before this it removed only the cake line, so its
 *  add-ons stayed in the cart, still billed, with no way to delete them — a customer had to
 *  abandon the site and start over to get rid of them.
 *
 *  Anything else (a candle, a legacy line) keeps the theme's original single-line path.
 */
if (!customElements.get('cart-remove')) {
  class CartRemove extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClickHandler.bind(this));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') this.onClickHandler(e);
      });
    }
    onClickHandler(event) {
      event.preventDefault();
      // Ask first. The trash sits in the quantity stepper now (it replaces "−" at quantity 1),
      // right where a thumb lands to change quantity, and a cake takes its add-ons with it.
      bhConfirmRemove(!!this.dataset.groupKeys).then((ok) => { if (ok) this.remove_(); });
    }
    remove_() {
      const index = this.dataset.index;
      const keys = (this.dataset.groupKeys || '').split(',').filter(Boolean);
      if (keys.length > 1) {
        this.closest('.bh-cart-group')?.classList.add('thb-loading', 'loading');
        bhRemoveKeys(keys, this.closest('cart-drawer'));
        return;
      }
      const cartDrawer = this.closest('cart-drawer');
      if (cartDrawer) {
        cartDrawer.updateQuantity(index, '0');
      } else {
        document.dispatchEvent(new CustomEvent('cart:remove', { detail: { index } }));
      }
    }
  }
  customElements.define('cart-remove', CartRemove);
}

/**
 * Resolves true only when the customer confirms in #BhRemoveConfirm
 * (snippets/bh-remove-confirm.liquid): a bottom sheet on phones, a centred popup on desktop.
 * Cancel, the backdrop, ESC or dragging the sheet away all resolve false. If the sheet is not on
 * the page (a template without the layout), removal goes ahead as before rather than breaking.
 */
function bhConfirmRemove(isCake) {
  const sheet = document.getElementById('BhRemoveConfirm');
  if (!sheet) return Promise.resolve(true);
  sheet.querySelectorAll('[data-bh-confirm-cake]').forEach((el) => { el.hidden = !isCake; });
  sheet.querySelectorAll('[data-bh-confirm-item]').forEach((el) => { el.hidden = isCake; });
  return new Promise((resolve) => {
    const ok = sheet.querySelector('[data-bh-confirm-ok]');
    const cancels = [...sheet.querySelectorAll('[data-bh-sheet-close]')]; // the X and Откажи
    const closeSheet = () => (sheet.__bhClose || (() => sheet.close()))();
    let done = false;
    // Settled from the buttons themselves; the dialog's `close` event is only a backstop
    // (backdrop tap, ESC, drag-to-dismiss), because bh-drawer.js notes it does not always fire.
    const settle = (value) => {
      if (done) return; done = true;
      ok.removeEventListener('click', onOk);
      cancels.forEach((c) => c.removeEventListener('click', onCancel));
      sheet.removeEventListener('close', onCancel);
      resolve(value);
    };
    const onOk = () => { settle(true); closeSheet(); };
    const onCancel = () => settle(false);
    ok.addEventListener('click', onOk);
    cancels.forEach((c) => c.addEventListener('click', onCancel));
    sheet.addEventListener('close', onCancel);
    if (sheet.__bhOpen) sheet.__bhOpen(document.activeElement); else sheet.showModal();
    // Clicks inside the sheet must not reach the mini-cart's "click outside closes me" handler.
    if (!sheet.__bhStopProp) { sheet.__bhStopProp = true; sheet.addEventListener('click', (e) => e.stopPropagation()); }
    // No auto-focus on Премахни: it drew a focus ring on phones and made the destructive button
    // the default. showModal() already moves focus into the sheet for keyboard users.
  });
}

/**
 * Zero a set of line keys and re-render EVERY section the host component normally refreshes
 * on a quantity change: the cart body, the header count and the header total. Uses the host's
 * own getSectionsToRender() so this can never update less than the theme's single-line delete.
 *
 * It used to fire `cart:refresh` instead, which on the cart page re-renders only the cart
 * body. The cake vanished but the header kept saying "4 items, €42", so the customer saw a
 * cart that looked like it had not changed.
 */
function bhRemoveKeys(keys, host) {
  host = host || document.querySelector('theme-cart') || document.querySelector('cart-drawer');
  const sections = host && host.getSectionsToRender ? host.getSectionsToRender() : [];
  const updates = {};
  keys.forEach((k) => { updates[k] = 0; });
  return fetch(`${theme.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      updates,
      sections: [...new Set(sections.map((sec) => sec.section))],
      sections_url: window.location.pathname
    })
  })
    .then((r) => r.json())
    .then((state) => {
      if (host && host.renderContents) {
        host.renderContents(state, null, false);
        document.dispatchEvent(new CustomEvent('line-item:change:end', { detail: { cart: state } }));
        return;
      }
      const parsed = {};
      sections.forEach((sec) => {
        const html = state.sections && state.sections[sec.section];
        if (!html) return;
        parsed[sec.section] = parsed[sec.section] || new DOMParser().parseFromString(html, 'text/html');
        const box = document.getElementById(sec.id);
        const target = box && (box.querySelector(sec.selector) || box);
        const source = parsed[sec.section].querySelector(sec.selector);
        if (target && source) target.innerHTML = source.innerHTML;
      });
      if (host && host.rebindFormListeners) host.rebindFormListeners();
      // Same event the theme fires after a quantity change; bh-cart-integrity.js listens to it.
      document.dispatchEvent(new CustomEvent('line-item:change:end', { detail: { cart: state } }));
    })
    .catch(() => window.location.reload()); // never leave a stale cart on screen
}

// Leftover add-ons whose cake is gone are removed by assets/bh-cart-integrity.js (the ONE place
// that does it). A second copy lived here briefly; two scripts sweeping the same cart race.
